/**
 * AssemblyReviewPage — Section-first assembly review.
 *
 * Primary view: sections list (reorderable), each containing clips
 * (reorderable, lockable, excludable). Per-section regenerate.
 * Secondary view: flat sequence timeline for fine ordering.
 *
 * UX contract: "AI proposes. You control."
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Film, GripVertical, GripHorizontal, X, Plus, Info,
  ChevronDown, Loader2, Download,
  Feather, Zap, Lock, Unlock, EyeOff, RefreshCw,
  Check, Layers, List, Trash2, LayoutGrid, AlertTriangle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import apiClient from '../services/api'
import { describeDegradations, isDegraded, isGenerationInProgress } from '../utils/generationStatus'
import ExportPanel from '../components/ExportPanel'
import SequencePlayer from '../components/SequencePlayer'
import MediaPreviewModal from '../components/MediaPreviewModal'
import { useMediaPreview } from '../hooks/useMediaPreview'
import { TimelineView } from '../components/TimelineView'

// Feature flag (Vite env, baked at build time). Off by default.
const TIMELINE_VIEW_ENABLED = import.meta.env.VITE_FEATURE_TIMELINE_VIEW === 'true'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  type DragStartEvent,
  type DragEndEvent,
  type CollisionDetection,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useDndSensors } from '../hooks/useDndSensors'
import { apiErrorDetail } from '../utils/dataValidator'

// ---------------------------------------------------------------------------
// Types — Section-first
// ---------------------------------------------------------------------------

interface SelectionReason {
  theme: string
  reason: string
  score: number
  score_breakdown: { audio: number; motion: number; speaker: number; total: number; brief?: number }
  source_asset_id?: number
  source_scene_index?: number
}

interface SectionMoment {
  id: number
  moment_id: number
  scene_index: number
  title: string | null
  visual_description: string | null
  tags: string[] | null
  transcript: string | null
  thumbnail_url: string | null
  start_time: number | null
  end_time: number | null
  duration: number | null
  asset_id: number | null
  asset_file_type: string | null
  asset_filename: string | null
  sequence_order: number
  is_selected: boolean
  is_locked: boolean
  is_excluded: boolean
  ai_score: number | null
  selection_reason: SelectionReason | null
}

interface Section {
  id: number
  name: string
  description: string | null
  section_type: string
  section_order: number
  is_locked: boolean
  cluster_label: string | null
  moments: SectionMoment[]
  moments_count: number
  selected_count: number
}

// Types — Sequence (secondary view)

interface Segment {
  id: number
  asset_id: number
  in_tc: number
  out_tc: number
  duration: number
  sequence_order: number
  removed: boolean
  removal_reason?: string | null
  ai_score: number | null
  selection_reason: SelectionReason | null
  source_moment_id: number | null
  thumbnail_url: string | null
  video_url: string | null
  asset_filename: string | null
  title: string | null
  visual_description: string | null
  tags: string[] | null
  transcript: string | null
}

interface SectionMarker {
  id: number
  label: string
  start_index: number
  end_index: number
  status: 'ok' | 'stale'
}

interface VersionData {
  id: number
  version_number: number
  style: string
  dirty: boolean
  segments: Segment[]
  section_markers: SectionMarker[]
  segments_count: number
  active_segments_count: number
  removed_segments_count: number
  total_duration: number
}

interface AssemblyDetail {
  id: number
  project_id: string
  name: string
  description: string | null
  status: string
  style: string | null
  generation_status: string
  generation_degradation?: string | null
  generation_error?: string | null
  sections: Section[]
  sections_count: number
  total_moments: number
  version: VersionData | null
  created_time: string | null
  updated_time: string | null
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTC(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// Sortable Clip Row (Section-first view)
// ---------------------------------------------------------------------------

function SortableClipRow({
  moment,
  sectionId,
  isSelected,
  onClick,
  onPreview,
  onToggleLock,
  onToggleExclude,
  onRemove,
}: {
  moment: SectionMoment
  sectionId: number
  isSelected: boolean
  onClick: () => void
  onPreview: () => void
  onToggleLock: () => void
  onToggleExclude: () => void
  onRemove: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `clip-${sectionId}-${moment.moment_id}`,
    data: { type: 'section-clip', sectionId, momentId: moment.moment_id },
    disabled: moment.is_locked,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : moment.is_excluded ? 0.4 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onClick()}
      className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
        isSelected
          ? 'bg-white/10 ring-1 ring-[var(--rq-blue)]/50'
          : 'hover:bg-white/[0.04]'
      } ${moment.is_locked ? 'ring-1 ring-amber-400/20' : ''}`}
    >
      {/* Drag handle */}
      {!moment.is_locked ? (
        <div
          {...attributes}
          {...listeners}
          className="flex-shrink-0 cursor-grab active:cursor-grabbing text-[var(--rq-text-soft)] hover:text-[var(--rq-text)]"
        >
          <GripVertical className="w-4 h-4" />
        </div>
      ) : (
        <div className="flex-shrink-0 w-4" />
      )}

      {/* Thumbnail — opens the preview. stopPropagation so it previews
          instead of also selecting the row, as the remove button does. */}
      <button
        type="button"
        onClick={e => { e.stopPropagation(); onPreview() }}
        title="Preview"
        className="w-16 h-9 rounded overflow-hidden bg-black/40 flex-shrink-0 hover:brightness-110 transition-all"
      >
        {moment.thumbnail_url ? (
          <img src={moment.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Film className="w-4 h-4 text-[var(--rq-text-soft)]" />
          </div>
        )}
      </button>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-[var(--rq-text)] truncate">
          {moment.title || moment.asset_filename || 'Untitled'}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          {moment.start_time != null && moment.end_time != null && (
            <span className="text-[10px] text-[var(--rq-text-soft)] tabular-nums">
              {formatTC(moment.start_time)} – {formatTC(moment.end_time)}
            </span>
          )}
          {moment.duration != null && (
            <span className="text-[10px] text-[var(--rq-text-soft)] tabular-nums">
              {formatTC(moment.duration)}
            </span>
          )}
          {moment.asset_filename && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-[var(--rq-text-soft)] truncate max-w-[100px]">
              {moment.asset_filename}
            </span>
          )}
        </div>
      </div>

      {/* Lock toggle */}
      <button
        onClick={e => { e.stopPropagation(); onToggleLock() }}
        className={`flex-shrink-0 p-1 transition-colors ${
          moment.is_locked
            ? 'text-amber-400 hover:text-amber-300'
            : 'text-[var(--rq-text-soft)] hover:text-amber-400'
        }`}
        title={moment.is_locked ? 'Unlock clip' : 'Lock clip'}
      >
        {moment.is_locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
      </button>

      {/* Exclude toggle */}
      <button
        onClick={e => { e.stopPropagation(); onToggleExclude() }}
        className={`flex-shrink-0 p-1 transition-colors ${
          moment.is_excluded
            ? 'text-orange-400'
            : 'text-[var(--rq-text-soft)] hover:text-orange-400'
        }`}
        title={moment.is_excluded ? 'Include clip' : 'Exclude from output'}
      >
        <EyeOff className="w-3.5 h-3.5" />
      </button>

      {/* Remove */}
      {!moment.is_locked && (
        <button
          onClick={e => { e.stopPropagation(); onRemove() }}
          className="flex-shrink-0 p-1 text-[var(--rq-text-soft)] hover:text-red-400 transition-colors"
          title="Remove from section"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Clip Row Overlay (for DnD)
// ---------------------------------------------------------------------------

function ClipRowOverlay({ moment }: { moment: SectionMoment }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[var(--rq-surface)] border border-white/20 shadow-xl">
      <GripVertical className="w-4 h-4 text-[var(--rq-text-soft)]" />
      <div className="w-16 h-9 rounded overflow-hidden bg-black/40 flex-shrink-0">
        {moment.thumbnail_url ? (
          <img src={moment.thumbnail_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Film className="w-4 h-4 text-[var(--rq-text-soft)]" />
          </div>
        )}
      </div>
      <p className="text-xs text-[var(--rq-text)] truncate">
        {moment.title || moment.asset_filename || 'Untitled'}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sortable Section Card
// ---------------------------------------------------------------------------

function SortableSectionCard({
  section,
  isExpanded,
  onToggleExpand,
  selectedClipId,
  onSelectClip,
  onPreviewClip,
  onToggleLock,
  onToggleExclude,
  onRemoveMoment,
  onToggleSectionLock,
  onRegenerate,
  onEditName,
  onDelete,
  regeneratingId,
}: {
  section: Section
  isExpanded: boolean
  onToggleExpand: () => void
  selectedClipId: number | null
  onSelectClip: (momentId: number) => void
  onPreviewClip: (moment: SectionMoment) => void
  onToggleLock: (sectionId: number, momentId: number) => void
  onToggleExclude: (sectionId: number, momentId: number) => void
  onRemoveMoment: (sectionId: number, momentId: number) => void
  onToggleSectionLock: (sectionId: number) => void
  onRegenerate: (sectionId: number) => void
  onEditName: (sectionId: number, currentName: string) => void
  onDelete: (sectionId: number) => void
  regeneratingId: number | null
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `section-${section.id}`,
    data: { type: 'section', sectionId: section.id },
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  }

  const orderedMoments = [...section.moments].sort((a, b) => a.sequence_order - b.sequence_order)
  const sectionDuration = orderedMoments.filter(m => !m.is_excluded).reduce((sum, m) => sum + (m.duration || 0), 0)
  const activeCount = orderedMoments.filter(m => !m.is_excluded).length
  const isRegenerating = regeneratingId === section.id

  return (
    <div ref={setNodeRef} style={style} className="rq-card border border-white/5">
      {/* Section header */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          className="flex-shrink-0 cursor-grab active:cursor-grabbing text-[var(--rq-text-soft)] hover:text-[var(--rq-text)]"
        >
          <GripVertical className="w-4 h-4" />
        </div>

        {/* Expand/collapse */}
        <button
          onClick={onToggleExpand}
          className="flex-shrink-0 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
        </button>

        {/* Name + stats */}
        <div className="flex-1 min-w-0">
          <button
            onClick={() => onEditName(section.id, section.name)}
            className="text-sm font-semibold text-[var(--rq-text)] hover:text-[var(--rq-blue)] transition-colors truncate block text-left"
            title="Click to rename"
          >
            {section.name}
          </button>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-[var(--rq-text-soft)]">
              {activeCount} clip{activeCount !== 1 ? 's' : ''} &middot; {formatTC(sectionDuration)}
            </span>
            {section.is_locked && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-400/10 text-amber-400">
                Locked
              </span>
            )}
          </div>
        </div>

        {/* Section actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => onToggleSectionLock(section.id)}
            className={`p-1.5 rounded transition-colors ${
              section.is_locked
                ? 'text-amber-400 hover:text-amber-300'
                : 'text-[var(--rq-text-soft)] hover:text-amber-400'
            }`}
            title={section.is_locked ? 'Unlock section' : 'Lock section'}
          >
            {section.is_locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
          </button>

          <button
            onClick={() => onRegenerate(section.id)}
            disabled={isRegenerating || section.is_locked}
            className="p-1.5 rounded text-[var(--rq-text-soft)] hover:text-[var(--rq-blue)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Regenerate section (locked clips preserved)"
          >
            <RefreshCw className={`w-4 h-4 ${isRegenerating ? 'animate-spin' : ''}`} />
          </button>

          <button
            onClick={() => onDelete(section.id)}
            className="p-1.5 rounded text-[var(--rq-text-soft)] hover:text-red-400 transition-colors"
            title="Delete section"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Clip list */}
      {isExpanded && (
        <div className="px-3 pb-3">
          {orderedMoments.length === 0 ? (
            <div className="text-center py-6 text-xs text-[var(--rq-text-soft)]">
              No clips in this section.
            </div>
          ) : (
            <SortableContext
              items={orderedMoments.map(m => `clip-${section.id}-${m.moment_id}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-0.5">
                {orderedMoments.map(moment => (
                  <SortableClipRow
                    key={moment.moment_id}
                    moment={moment}
                    sectionId={section.id}
                    isSelected={selectedClipId === moment.moment_id}
                    onClick={() => onSelectClip(moment.moment_id)}
                    onPreview={() => onPreviewClip(moment)}
                    onToggleLock={() => onToggleLock(section.id, moment.moment_id)}
                    onToggleExclude={() => onToggleExclude(section.id, moment.moment_id)}
                    onRemove={() => onRemoveMoment(section.id, moment.moment_id)}
                  />
                ))}
              </div>
            </SortableContext>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sortable Segment Card (Sequence view — kept from original)
// ---------------------------------------------------------------------------

function SortableSegmentCard({
  segment,
  index,
  isSelected,
  sectionLabel,
  onClick,
  onPreview,
  onRemove,
}: {
  segment: Segment
  index: number
  isSelected: boolean
  sectionLabel: string | null
  onClick: () => void
  onPreview: () => void
  onRemove: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `seg-${segment.id}`,
    data: { type: 'segment', segmentId: segment.id },
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onClick()}
      className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
        isSelected
          ? 'bg-white/10 ring-1 ring-[var(--rq-blue)]/50'
          : 'hover:bg-white/[0.04]'
      }`}
    >
      <div
        {...attributes}
        {...listeners}
        className="flex-shrink-0 cursor-grab active:cursor-grabbing text-[var(--rq-text-soft)] hover:text-[var(--rq-text)]"
      >
        <GripVertical className="w-4 h-4" />
      </div>
      <span className="text-[10px] text-[var(--rq-text-soft)] w-5 text-right tabular-nums flex-shrink-0">
        {index + 1}
      </span>
      <button
        type="button"
        onClick={e => { e.stopPropagation(); onPreview() }}
        title="Preview"
        className="w-16 h-9 rounded overflow-hidden bg-black/40 flex-shrink-0 hover:brightness-110 transition-all"
      >
        {segment.thumbnail_url ? (
          <img src={segment.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Film className="w-4 h-4 text-[var(--rq-text-soft)]" />
          </div>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-[var(--rq-text)] truncate">
          {segment.title || segment.asset_filename || 'Untitled'}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-[var(--rq-text-soft)] tabular-nums">
            {formatTC(segment.in_tc)} – {formatTC(segment.out_tc)}
          </span>
          <span className="text-[10px] text-[var(--rq-text-soft)]">
            {formatTC(segment.duration)}
          </span>
          {sectionLabel && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-[var(--rq-text-soft)]">
              {sectionLabel}
            </span>
          )}
        </div>
      </div>
      <button
        onClick={e => { e.stopPropagation(); onRemove() }}
        className="flex-shrink-0 p-1 text-[var(--rq-text-soft)] hover:text-red-400 transition-colors"
        title="Remove from sequence"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

function SegmentOverlay({ segment }: { segment: Segment }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[var(--rq-surface)] border border-white/20 shadow-xl">
      <GripVertical className="w-4 h-4 text-[var(--rq-text-soft)]" />
      <div className="w-16 h-9 rounded overflow-hidden bg-black/40 flex-shrink-0">
        {segment.thumbnail_url ? (
          <img src={segment.thumbnail_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Film className="w-4 h-4 text-[var(--rq-text-soft)]" />
          </div>
        )}
      </div>
      <p className="text-xs text-[var(--rq-text)] truncate">
        {segment.title || segment.asset_filename || 'Untitled'}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Horizontal Timeline Block (sortable)
// ---------------------------------------------------------------------------

const TIMELINE_COLORS = [
  'bg-[var(--rq-blue)]/20 border-[var(--rq-blue)]/40',
  'bg-emerald-500/20 border-emerald-500/40',
  'bg-violet-500/20 border-violet-500/40',
  'bg-rose-500/20 border-rose-500/40',
  'bg-cyan-500/20 border-cyan-500/40',
  'bg-amber-500/20 border-amber-500/40',
]

function SortableTimelineBlock({
  segment,
  index,
  widthPct,
  isSelected,
  sectionLabel,
  sectionIndex,
  onClick,
  onPreview,
  onRemove,
}: {
  segment: Segment
  index: number
  widthPct: number
  isSelected: boolean
  sectionLabel: string | null
  sectionIndex: number
  onClick: () => void
  onPreview: () => void
  onRemove: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `seg-${segment.id}`,
    data: { type: 'segment', segmentId: segment.id },
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    width: `${widthPct}%`,
    minWidth: '60px',
  }

  const colorClass = TIMELINE_COLORS[sectionIndex % TIMELINE_COLORS.length]

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onClick()}
      className={`relative group flex-shrink-0 h-full border rounded-md cursor-pointer transition-colors overflow-hidden ${colorClass} ${
        isSelected ? 'ring-2 ring-[var(--rq-blue)] ring-offset-1 ring-offset-[var(--rq-bg)]' : ''
      }`}
    >
      {/* Thumbnail background */}
      {segment.thumbnail_url && (
        <img
          src={segment.thumbnail_url}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          loading="lazy"
        />
      )}

      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-0.5 left-0.5 cursor-grab active:cursor-grabbing text-white/50 hover:text-white/80 z-10"
      >
        <GripHorizontal className="w-3 h-3" />
      </div>

      {/* Preview button. The thumbnail here is a background layer behind the
          drag handle, so it cannot be the click target the way it is in the
          list rows. Sits beside remove, on the same hover reveal. */}
      <button
        onClick={e => { e.stopPropagation(); onPreview() }}
        className="absolute top-0.5 right-5 w-4 h-4 rounded-full bg-black/60 text-white/60 hover:text-white hover:bg-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
        title="Preview"
      >
        <Film className="w-2.5 h-2.5" />
      </button>

      {/* Remove button */}
      <button
        onClick={e => { e.stopPropagation(); onRemove() }}
        className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/60 text-white/60 hover:text-white hover:bg-red-500/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
        title="Remove"
      >
        <X className="w-2.5 h-2.5" />
      </button>

      {/* Content overlay */}
      <div className="relative z-[1] h-full flex flex-col justify-end p-1.5">
        <p className="text-[9px] text-white font-medium truncate leading-tight drop-shadow-md">
          {segment.title || segment.asset_filename || 'Untitled'}
        </p>
        <div className="flex items-center gap-1 mt-0.5">
          <span className="text-[8px] text-white/70 tabular-nums drop-shadow-md">
            {formatTC(segment.duration)}
          </span>
          {sectionLabel && (
            <span className="text-[7px] px-1 py-px rounded bg-black/30 text-white/60 truncate">
              {sectionLabel}
            </span>
          )}
        </div>
      </div>

      {/* Index badge */}
      <div className="absolute top-0.5 left-1/2 -translate-x-1/2 text-[8px] text-white/50 tabular-nums z-10">
        {index + 1}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Right Panel — Clip Details (supports both section moments and segments)
// ---------------------------------------------------------------------------

function ClipDetailsPanel({
  clip,
  sectionName,
  onPreview,
}: {
  clip: {
    title: string | null
    asset_filename: string | null
    thumbnail_url: string | null
    video_url?: string | null
    visual_description?: string | null
    tags?: string[] | null
    transcript?: string | null
    start_time?: number | null
    end_time?: number | null
    in_tc?: number
    out_tc?: number
    duration: number | null
    selection_reason: SelectionReason | null
  }
  sectionName: string | null
  onPreview: () => void
}) {
  const inTime = clip.in_tc ?? clip.start_time
  const outTime = clip.out_tc ?? clip.end_time

  return (
    <div className="rounded-xl border border-white/[0.06] bg-[var(--rq-surface)] overflow-hidden">
      {/* Thumbnail */}
      <button
        onClick={onPreview}
        className="aspect-video w-full overflow-hidden bg-black/40 block hover:brightness-110 transition-all relative group"
        title="Preview"
      >
        {clip.thumbnail_url ? (
          <img src={clip.thumbnail_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Film className="w-8 h-8 text-[var(--rq-text-soft)]" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        {/* Timecode bar overlaid at bottom of thumbnail */}
        <div className="absolute bottom-0 inset-x-0 flex items-center justify-between px-3 py-2">
          {inTime != null && outTime != null ? (
            <span className="text-[10px] text-white/70 tabular-nums font-medium">
              {formatTC(inTime)} – {formatTC(outTime)}
            </span>
          ) : <span />}
          {clip.duration != null && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/50 text-white/90 tabular-nums font-semibold">
              {formatTC(clip.duration)}
            </span>
          )}
        </div>
      </button>

      {/* Content */}
      <div className="px-4 py-3 space-y-3">
        {/* Title + source */}
        <div>
          <h3 className="text-[13px] font-semibold text-[var(--rq-text)] leading-snug">
            {clip.title || clip.asset_filename || 'Untitled'}
          </h3>
          {clip.asset_filename && clip.title && (
            <p className="text-[10px] text-[var(--rq-text-soft)] mt-0.5 truncate">{clip.asset_filename}</p>
          )}
        </div>

        {/* Section badge */}
        {sectionName && (
          <div className="inline-flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-md bg-[var(--rq-blue)]/10 border border-[var(--rq-blue)]/20 text-[var(--rq-blue)]">
            <Layers className="w-3 h-3" />
            {sectionName}
          </div>
        )}

        {/* Summary */}
        {clip.visual_description && (
          <p className="text-xs text-[var(--rq-text-soft)] leading-relaxed">
            {clip.visual_description}
          </p>
        )}

        {/* Tags */}
        {clip.tags && clip.tags.length > 0 && (() => {
          const tagColors = [
            'bg-[var(--rq-blue)]/10 border-[var(--rq-blue)]/20 text-[var(--rq-blue)]',
            'bg-[var(--rq-amber)]/10 border-[var(--rq-amber)]/20 text-[var(--rq-amber)]',
            'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
            'bg-violet-500/10 border-violet-500/20 text-violet-400',
            'bg-rose-500/10 border-rose-500/20 text-rose-400',
            'bg-cyan-500/10 border-cyan-500/20 text-cyan-400',
          ]
          return (
            <div className="flex flex-wrap gap-1">
              {clip.tags.map((tag, i) => (
                <span
                  key={i}
                  className={`text-[10px] px-2 py-0.5 rounded-full border ${tagColors[i % tagColors.length]}`}
                >
                  {tag}
                </span>
              ))}
            </div>
          )
        })()}

        {/* Transcript */}
        {clip.transcript && (
          <div className="border-l-2 border-white/10 pl-3">
            <p className="text-[11px] text-[var(--rq-text-soft)] leading-relaxed italic">
              &ldquo;{clip.transcript}&rdquo;
            </p>
          </div>
        )}

        {/* Why this clip */}
        {clip.selection_reason && (
          <div className="pt-2 border-t border-white/[0.06] space-y-1">
            <p className="text-[10px] font-semibold text-[var(--rq-text-soft)] uppercase tracking-wider">Why this clip</p>
            <p className="text-[11px] text-[var(--rq-text-soft)] leading-relaxed">
              {clip.selection_reason.reason}
            </p>
            {clip.selection_reason.theme && (
              <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-[var(--rq-text-soft)]">
                {clip.selection_reason.theme}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function AssemblyReviewPage() {
  const { assemblyId } = useParams<{ assemblyId: string }>()
  const navigate = useNavigate()
  const sensors = useDndSensors()

  const [assembly, setAssembly] = useState<AssemblyDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // View mode
  const [viewMode, setViewMode] = useState<'sections' | 'sequence'>('sequence')

  // Section-first state
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set())
  const [selectedClipMomentId, setSelectedClipMomentId] = useState<number | null>(null)
  const [draggedClipMoment, setDraggedClipMoment] = useState<SectionMoment | null>(null)
  const [regeneratingSection, setRegeneratingSection] = useState<number | null>(null)
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null)
  const [editSectionName, setEditSectionName] = useState('')

  // Sequence view state (secondary)
  const [activeSegments, setActiveSegments] = useState<Segment[]>([])
  const [removedSegments, setRemovedSegments] = useState<Segment[]>([])
  const [selectedSegmentId, setSelectedSegmentId] = useState<number | null>(null)
  const [showRemovedPool, setShowRemovedPool] = useState(false)
  const [draggedSegment, setDraggedSegment] = useState<Segment | null>(null)
  const [sequenceLayout, setSequenceLayout] = useState<'list' | 'timeline'>('timeline')

  // Shared UI state
  const [showExport, setShowExport] = useState(false)
  // Where playback is, in the source file rather than in the assembled
  // sequence. The timeline below is drawn in one asset's own time, so it
  // needs both the position and which asset it belongs to. Null until the
  // player reports, and while nothing is playing.
  const [playhead, setPlayhead] = useState<
    { assetId: number; sourceTime: number } | null
  >(null)
  const { preview, closePreview, openImage, openVideo, openFromAsset } = useMediaPreview()

  // ---------------------------------------------------------------------------
  // Fetch
  // ---------------------------------------------------------------------------

  const fetchAssembly = useCallback(async () => {
    if (!assemblyId) return
    try {
      const res = await apiClient.get(`/assemblies/${assemblyId}`)
      const data = res.data as AssemblyDetail
      setAssembly(data)
      setError(null)

      // Expand all sections on first load; prune stale IDs after regeneration
      if (data.sections?.length > 0) {
        setExpandedSections(prev => {
          if (prev.size === 0) return new Set(data.sections.map(s => s.id))
          const validIds = new Set(data.sections.map(s => s.id))
          const next = new Set([...prev].filter(id => validIds.has(id)))
          return next.size === 0 ? new Set(data.sections.map(s => s.id)) : next
        })
      }

      // Populate sequence view data
      if (data.version) {
        const active = data.version.segments
          .filter(s => !s.removed)
          .sort((a, b) => a.sequence_order - b.sequence_order)
        const removed = data.version.segments
          .filter(s => s.removed)
          .sort((a, b) => a.sequence_order - b.sequence_order)
        setActiveSegments(active)
        setRemovedSegments(removed)
      }
    } catch (err) {
      setError(apiErrorDetail(err, 'Failed to load assembly'))
    } finally {
      setLoading(false)
    }
  }, [assemblyId])

  useEffect(() => { fetchAssembly() }, [fetchAssembly])

  // Redirect if still generating
  const generationStatus = assembly?.generation_status
  useEffect(() => {
    if (isGenerationInProgress(generationStatus)) {
      navigate(`/assemblies/${assemblyId}/processing`, { replace: true })
    }
  }, [generationStatus, navigate, assemblyId])


  // ---------------------------------------------------------------------------
  // Section-first handlers
  // ---------------------------------------------------------------------------

  const handleToggleLock = async (sectionId: number, momentId: number) => {
    if (!assembly) return
    setAssembly(prev => {
      if (!prev) return prev
      return {
        ...prev,
        sections: prev.sections.map(s => {
          if (s.id !== sectionId) return s
          return { ...s, moments: s.moments.map(m =>
            m.moment_id === momentId ? { ...m, is_locked: !m.is_locked } : m
          )}
        }),
      }
    })
    try {
      await apiClient.patch(`/assemblies/${assembly.id}/sections/${sectionId}/moments/${momentId}/lock`)
    } catch {
      toast.error('Failed to toggle lock')
      fetchAssembly()
    }
  }

  const handleToggleExclude = async (sectionId: number, momentId: number) => {
    if (!assembly) return
    const assemblyId = assembly.id
    setAssembly(prev => {
      if (!prev) return prev
      return {
        ...prev,
        sections: prev.sections.map(s => {
          if (s.id !== sectionId) return s
          return { ...s, moments: s.moments.map(m =>
            m.moment_id === momentId ? { ...m, is_excluded: !m.is_excluded } : m
          )}
        }),
      }
    })
    try {
      await apiClient.patch(`/assemblies/${assemblyId}/sections/${sectionId}/moments/${momentId}/exclude`)
    } catch {
      toast.error('Failed to toggle exclusion')
      fetchAssembly()
    }
  }

  const handleRemoveMoment = async (sectionId: number, momentId: number) => {
    if (!assembly) return
    // Optimistic
    setAssembly(prev => {
      if (!prev) return prev
      return {
        ...prev,
        sections: prev.sections.map(s => {
          if (s.id !== sectionId) return s
          return { ...s, moments: s.moments.filter(m => m.moment_id !== momentId), moments_count: s.moments_count - 1 }
        }),
      }
    })
    if (selectedClipMomentId === momentId) setSelectedClipMomentId(null)
    try {
      await apiClient.delete(`/assemblies/${assembly.id}/sections/${sectionId}/moments/${momentId}`)
    } catch {
      toast.error('Failed to remove clip')
      fetchAssembly()
    }
  }

  const handleToggleSectionLock = async (sectionId: number) => {
    if (!assembly) return
    setAssembly(prev => {
      if (!prev) return prev
      return {
        ...prev,
        sections: prev.sections.map(s =>
          s.id === sectionId ? { ...s, is_locked: !s.is_locked } : s
        ),
      }
    })
    try {
      await apiClient.patch(`/assemblies/${assembly.id}/sections/${sectionId}/lock`)
    } catch {
      toast.error('Failed to toggle section lock')
      fetchAssembly()
    }
  }

  const handleRegenerateSection = async (sectionId: number) => {
    if (!assembly || regeneratingSection !== null) return
    setRegeneratingSection(sectionId)
    try {
      await apiClient.post(`/assemblies/${assembly.id}/sections/${sectionId}/regenerate`)
      toast.success('Section regenerated')
      fetchAssembly()
    } catch {
      toast.error('Failed to regenerate section')
    } finally {
      setRegeneratingSection(null)
    }
  }

  const handleEditSectionName = (sectionId: number, currentName: string) => {
    setEditingSectionId(sectionId)
    setEditSectionName(currentName)
  }

  const handleSaveSectionName = async () => {
    if (!editSectionName.trim() || !assembly || editingSectionId === null) return
    try {
      await apiClient.patch(`/assemblies/${assembly.id}/sections/${editingSectionId}`, {
        name: editSectionName.trim(),
      })
      setEditingSectionId(null)
      fetchAssembly()
    } catch {
      toast.error('Failed to rename section')
    }
  }

  const handleAddSection = async () => {
    if (!assembly) return
    const name = `Section ${(assembly.sections?.length || 0) + 1}`
    try {
      const res = await apiClient.post(`/assemblies/${assembly.id}/sections`, { name })
      toast.success('Section added')
      fetchAssembly()
      // Auto-expand the new section
      if (res.data?.id) {
        setExpandedSections(prev => new Set([...prev, res.data.id]))
      }
    } catch {
      toast.error('Failed to add section')
    }
  }

  const handleDeleteSection = async (sectionId: number) => {
    if (!assembly) return
    const section = assembly.sections.find(s => s.id === sectionId)
    const clipCount = section?.moments?.length || 0
    const msg = clipCount > 0
      ? `Delete "${section?.name}" and its ${clipCount} clip${clipCount !== 1 ? 's' : ''}?`
      : `Delete empty section "${section?.name}"?`
    if (!confirm(msg)) return

    // Optimistic removal
    setAssembly(prev => {
      if (!prev) return prev
      return { ...prev, sections: prev.sections.filter(s => s.id !== sectionId) }
    })
    try {
      await apiClient.delete(`/assemblies/${assembly.id}/sections/${sectionId}`)
      toast.success('Section deleted')
      fetchAssembly()
    } catch {
      toast.error('Failed to delete section')
      fetchAssembly()
    }
  }

  // Custom collision detection: sections only drop on sections, clips on clips or sections
  const sectionAwareCollision: CollisionDetection = useCallback((args) => {
    const activeType = args.active.data.current?.type
    if (activeType === 'section') {
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter(
          c => c.data.current?.type === 'section'
        ),
      })
    }
    return closestCenter(args)
  }, [])

  // Unified DnD handlers for sections view
  const handleSectionsDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current
    if (data?.type === 'section-clip' && assembly) {
      const section = assembly.sections.find(s => s.id === data.sectionId)
      const moment = section?.moments.find(m => m.moment_id === data.momentId)
      setDraggedClipMoment(moment || null)
    }
  }

  const handleSectionsDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    const activeData = active.data.current
    setDraggedClipMoment(null)
    if (!assembly || !over || active.id === over.id) return
    const overData = over.data.current

    // --- Section reorder ---
    if (activeData?.type === 'section' && overData?.type === 'section') {
      const sorted = [...assembly.sections].sort((a, b) => a.section_order - b.section_order)
      const oldIdx = sorted.findIndex(s => `section-${s.id}` === active.id)
      const newIdx = sorted.findIndex(s => `section-${s.id}` === over.id)
      if (oldIdx === -1 || newIdx === -1) return
      const reordered = arrayMove(sorted, oldIdx, newIdx).map((s, i) => ({ ...s, section_order: i }))
      setAssembly(prev => prev ? { ...prev, sections: reordered } : prev)
      try {
        await apiClient.patch(`/assemblies/${assembly.id}/sections/reorder`, {
          section_ids: reordered.map(s => s.id),
        })
      } catch {
        toast.error('Failed to reorder sections')
        fetchAssembly()
      }
      return
    }

    // --- Clip drag ---
    if (activeData?.type !== 'section-clip') return
    const sourceSectionId: number = activeData.sectionId
    const momentId: number = activeData.momentId

    // Determine target section and insert index
    let targetSectionId: number
    let insertIndex: number

    if (overData?.type === 'section-clip') {
      targetSectionId = overData.sectionId
      const targetSection = assembly.sections.find(s => s.id === targetSectionId)
      if (!targetSection) return
      const targetOrdered = [...targetSection.moments].sort((a, b) => a.sequence_order - b.sequence_order)
      insertIndex = targetOrdered.findIndex(m => m.moment_id === overData.momentId)
      if (insertIndex === -1) insertIndex = targetOrdered.length
    } else if (overData?.type === 'section') {
      targetSectionId = overData.sectionId
      const targetSection = assembly.sections.find(s => s.id === targetSectionId)
      insertIndex = targetSection ? targetSection.moments.length : 0
    } else {
      return
    }

    if (sourceSectionId === targetSectionId) {
      // Same-section reorder
      const section = assembly.sections.find(s => s.id === sourceSectionId)
      if (!section) return
      const ordered = [...section.moments].sort((a, b) => a.sequence_order - b.sequence_order)
      const oldIdx = ordered.findIndex(m => `clip-${sourceSectionId}-${m.moment_id}` === active.id)
      const newIdx = ordered.findIndex(m => `clip-${sourceSectionId}-${m.moment_id}` === over.id)
      if (oldIdx === -1 || newIdx === -1) return
      const newOrder = arrayMove(ordered.map(m => m.moment_id), oldIdx, newIdx)
      const newMoments = newOrder.map((mid, i) => {
        const m = ordered.find(om => om.moment_id === mid)!
        return { ...m, sequence_order: i }
      })
      setAssembly(prev => {
        if (!prev) return prev
        return { ...prev, sections: prev.sections.map(s =>
          s.id === sourceSectionId ? { ...s, moments: newMoments } : s
        )}
      })
      try {
        await apiClient.patch(`/assemblies/${assembly.id}/sections/${sourceSectionId}/reorder`, {
          moment_ids: newOrder,
        })
      } catch {
        toast.error('Failed to reorder clips')
        fetchAssembly()
      }
    } else {
      // Cross-section move
      const sourceSection = assembly.sections.find(s => s.id === sourceSectionId)
      const movingMoment = sourceSection?.moments.find(m => m.moment_id === momentId)
      if (!movingMoment) return

      // Optimistic update: remove from source, add to target
      setAssembly(prev => {
        if (!prev) return prev
        return {
          ...prev,
          sections: prev.sections.map(s => {
            if (s.id === sourceSectionId) {
              const remaining = s.moments
                .filter(m => m.moment_id !== momentId)
                .sort((a, b) => a.sequence_order - b.sequence_order)
                .map((m, i) => ({ ...m, sequence_order: i }))
              return { ...s, moments: remaining, moments_count: remaining.length }
            }
            if (s.id === targetSectionId) {
              const existing = [...s.moments].sort((a, b) => a.sequence_order - b.sequence_order)
              const inserted = [
                ...existing.slice(0, insertIndex),
                { ...movingMoment, sequence_order: insertIndex },
                ...existing.slice(insertIndex),
              ].map((m, i) => ({ ...m, sequence_order: i }))
              return { ...s, moments: inserted, moments_count: inserted.length }
            }
            return s
          }),
        }
      })

      try {
        await apiClient.patch(`/assemblies/${assembly.id}/sections/${targetSectionId}/moments/move`, {
          moment_id: momentId,
          from_section_id: sourceSectionId,
          insert_index: insertIndex,
        })
        fetchAssembly()
      } catch {
        toast.error('Failed to move clip')
        fetchAssembly()
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Sequence view handlers (kept from original)
  // ---------------------------------------------------------------------------

  // Map each segment to its section via source_moment_id → section moments
  const segmentSectionMap = useMemo(() => {
    if (!assembly) return new Map<number, { label: string; index: number }>()
    const momentToSection = new Map<number, { label: string; index: number }>()
    const sorted = [...assembly.sections].sort((a, b) => a.section_order - b.section_order)
    sorted.forEach((s, i) => {
      s.moments.forEach(m => momentToSection.set(m.moment_id, { label: s.name, index: i }))
    })
    const result = new Map<number, { label: string; index: number }>()
    activeSegments.forEach(seg => {
      if (seg.source_moment_id) {
        const info = momentToSection.get(seg.source_moment_id)
        if (info) result.set(seg.id, info)
      }
    })
    return result
  }, [assembly, activeSegments])

  const getSectionLabel = useCallback((_segIdx: number): string | null => {
    const seg = activeSegments[_segIdx]
    if (!seg) return null
    return segmentSectionMap.get(seg.id)?.label ?? null
  }, [activeSegments, segmentSectionMap])

  const getSectionIndex = useCallback((_segIdx: number): number => {
    const seg = activeSegments[_segIdx]
    if (!seg) return 0
    return segmentSectionMap.get(seg.id)?.index ?? 0
  }, [activeSegments, segmentSectionMap])

  const handleRemoveSegment = async (segmentId: number) => {
    const seg = activeSegments.find(s => s.id === segmentId)
    if (!seg) return
    setActiveSegments(prev => prev.filter(s => s.id !== segmentId))
    setRemovedSegments(prev => [...prev, { ...seg, removed: true }])
    if (selectedSegmentId === segmentId) setSelectedSegmentId(null)
    try {
      await apiClient.patch(`/assemblies/${assembly!.id}/segments/${segmentId}/remove`)
      fetchAssembly()
    } catch {
      toast.error('Failed to remove segment')
      fetchAssembly()
    }
  }

  const handleRestoreSegment = async (segmentId: number) => {
    const seg = removedSegments.find(s => s.id === segmentId)
    if (!seg) return
    setRemovedSegments(prev => prev.filter(s => s.id !== segmentId))
    setActiveSegments(prev => [...prev, { ...seg, removed: false, sequence_order: prev.length }])
    try {
      await apiClient.patch(`/assemblies/${assembly!.id}/segments/${segmentId}/restore`)
      fetchAssembly()
    } catch {
      toast.error('Failed to restore segment')
      fetchAssembly()
    }
  }

  const handleSegDragStart = (event: DragStartEvent) => {
    const segId = Number(String(event.active.id).replace('seg-', ''))
    setDraggedSegment(activeSegments.find(s => s.id === segId) || null)
  }

  const handleSegDragEnd = async (event: DragEndEvent) => {
    setDraggedSegment(null)
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIdx = activeSegments.findIndex(s => `seg-${s.id}` === active.id)
    const newIdx = activeSegments.findIndex(s => `seg-${s.id}` === over.id)
    if (oldIdx === -1 || newIdx === -1) return
    const reordered = arrayMove(activeSegments, oldIdx, newIdx).map((s, i) => ({ ...s, sequence_order: i }))
    setActiveSegments(reordered)
    try {
      await apiClient.patch(`/assemblies/${assembly!.id}/segments/reorder`, {
        segment_ids: reordered.map(s => s.id),
      })
      fetchAssembly()
    } catch {
      toast.error('Failed to reorder')
      fetchAssembly()
    }
  }

  // ---------------------------------------------------------------------------
  // Preview handler
  // ---------------------------------------------------------------------------

  const handlePreviewClip = useCallback(async (clip: {
    thumbnail_url: string | null
    video_url?: string | null
    title: string | null
    asset_filename: string | null
    asset_id?: number | null
    asset_file_type?: string | null
    // Segment-style in/out timecodes
    in_tc?: number
    out_tc?: number
    // Moment-style timecodes (used elsewhere)
    start_time?: number | null
    end_time?: number | null
  }) => {
    const title = clip.title || clip.asset_filename || 'Preview'
    const startTime = clip.in_tc ?? clip.start_time ?? undefined
    const endTime = clip.out_tc ?? clip.end_time ?? undefined
    if (clip.asset_id) {
      await openFromAsset({
        assetId: clip.asset_id,
        fileType: clip.asset_file_type,
        title,
        fallbackImageUrl: clip.thumbnail_url || null,
        startTime: typeof startTime === 'number' ? startTime : undefined,
        endTime: typeof endTime === 'number' ? endTime : undefined,
      })
      return
    }
    if (clip.video_url) {
      openVideo(clip.video_url, title, undefined, {
        startTime: typeof startTime === 'number' ? startTime : undefined,
        endTime: typeof endTime === 'number' ? endTime : undefined,
      })
      return
    }
    if (clip.thumbnail_url) { openImage(clip.thumbnail_url, title); return }
    toast.error('No preview available')
  }, [openFromAsset, openImage, openVideo])

  // ---------------------------------------------------------------------------
  // Computed
  // ---------------------------------------------------------------------------

  const sections = assembly?.sections
    ? [...assembly.sections].sort((a, b) => a.section_order - b.section_order)
    : []

  const totalClips = sections.reduce((sum, s) => sum + s.moments.filter(m => !m.is_excluded).length, 0)
  const totalDuration = viewMode === 'sections'
    ? sections.reduce((sum, s) => sum + s.moments.filter(m => !m.is_excluded).reduce((d, m) => d + (m.duration || 0), 0), 0)
    : activeSegments.reduce((sum, s) => sum + s.duration, 0)

  const styleBadge = assembly?.style === 'compressed'
    ? { icon: Zap, label: 'Compressed', color: 'text-amber-400 bg-amber-400/10 border-amber-500/30' }
    : assembly?.style === 'balanced'
      ? { icon: Feather, label: 'Balanced', color: 'text-emerald-400 bg-emerald-400/10 border-emerald-500/30' }
      : { icon: Feather, label: 'Conservative', color: 'text-sky-300 bg-sky-400/10 border-sky-500/30' }

  // Find selected clip across all sections
  const selectedClip: SectionMoment | null = (() => {
    if (!selectedClipMomentId || !assembly) return null
    for (const s of assembly.sections) {
      const m = s.moments.find(m => m.moment_id === selectedClipMomentId)
      if (m) return m
    }
    return null
  })()

  const selectedClipSection: Section | null = (() => {
    if (!selectedClipMomentId || !assembly) return null
    return assembly.sections.find(s => s.moments.some(m => m.moment_id === selectedClipMomentId)) || null
  })()

  const selectedSegment = activeSegments.find(s => s.id === selectedSegmentId)
    || removedSegments.find(s => s.id === selectedSegmentId)
    || null

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="rq-page flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--rq-text-soft)]" />
      </div>
    )
  }

  if (error || !assembly) {
    return (
      <div className="rq-page">
        <p className="text-red-400">{error || 'Assembly not found'}</p>
        <button onClick={() => navigate('/assemblies')} className="rq-btn-ghost px-4 py-2 text-sm mt-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>
    )
  }

  if (isGenerationInProgress(assembly.generation_status)) {
    return (
      <div className="rq-page flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--rq-text-soft)]" />
      </div>
    )
  }

  return (
    <div className="rq-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate('/assemblies')} className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors flex-shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-[var(--rq-text)] truncate">{assembly.name}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${styleBadge.color}`}>
                <styleBadge.icon className="w-3 h-3" />
                {styleBadge.label}
              </span>
              <span className="text-[10px] text-[var(--rq-text-soft)]">
                {totalClips} clip{totalClips !== 1 ? 's' : ''} &middot; {formatTC(totalDuration)}
              </span>
            </div>
            <p className="text-[10px] text-[var(--rq-text-soft)] mt-0.5 italic">
              AI proposes. You control.
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowExport(!showExport)}
          className="rq-btn-primary px-3 py-1.5 text-xs"
        >
          <Download className="w-3.5 h-3.5" />
          Export
        </button>
      </div>

      {/* Degraded draft notice. Shown before the clips, because it changes
          how much of the review to trust, and after the fact is too late. */}
      {isDegraded(assembly.generation_status) && (
        <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-xs font-medium text-[var(--rq-text)]">
                This draft was completed through a fallback
              </p>
              <ul className="mt-1 space-y-0.5">
                {describeDegradations(assembly.generation_degradation).map((reason) => (
                  <li key={reason} className="text-[11px] text-[var(--rq-text-soft)]">
                    {reason}
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-[var(--rq-text-soft)] mt-1">
                It is complete and yours to edit. Regenerate if you want another attempt.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Export panel */}
      {showExport && (
        <div className="mb-4">
          <ExportPanel
            assemblyId={assembly.id}
            assemblyName={assembly.name}
            clipCount={totalClips}
            totalDuration={totalDuration}
          />
        </div>
      )}

      {/* Tab bar */}
      <div className="flex items-center gap-1 mb-4 border-b border-white/10 pb-2">
        <button
          onClick={() => setViewMode('sequence')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            viewMode === 'sequence'
              ? 'bg-white/10 text-[var(--rq-text)]'
              : 'text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] hover:bg-white/5'
          }`}
        >
          <List className="w-3.5 h-3.5" />
          Sequence
        </button>
        <button
          onClick={() => setViewMode('sections')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            viewMode === 'sections'
              ? 'bg-white/10 text-[var(--rq-text)]'
              : 'text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] hover:bg-white/5'
          }`}
        >
          <Layers className="w-3.5 h-3.5" />
          Sections
        </button>
      </div>

      {/* Section name edit modal */}
      {editingSectionId !== null && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center" onClick={() => setEditingSectionId(null)}>
          <div className="bg-[var(--rq-surface)] border border-white/10 rounded-xl p-4 w-80 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-[var(--rq-text)] mb-3">Rename Section</h3>
            <input
              type="text"
              value={editSectionName}
              onChange={e => setEditSectionName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveSectionName(); if (e.key === 'Escape') setEditingSectionId(null) }}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-[var(--rq-text)] focus:outline-none focus:border-[var(--rq-blue)]/50"
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setEditingSectionId(null)} className="px-3 py-1.5 text-xs text-[var(--rq-text-soft)] hover:text-[var(--rq-text)]">
                Cancel
              </button>
              <button onClick={handleSaveSectionName} className="rq-btn-primary px-3 py-1.5 text-xs">
                <Check className="w-3 h-3" />
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main layout */}
      <div className="flex gap-6">
        {/* Left content area */}
        <div className="flex-1 min-w-0 space-y-4">

          {/* === SECTIONS VIEW === */}
          {viewMode === 'sections' && (
            <>
              {sections.length === 0 ? (
                <div className="text-center py-12 text-[var(--rq-text-soft)] text-sm">
                  No sections yet.
                </div>
              ) : (
                <DndContext
                  id="sections-unified-dnd"
                  sensors={sensors}
                  collisionDetection={sectionAwareCollision}
                  onDragStart={handleSectionsDragStart}
                  onDragEnd={handleSectionsDragEnd}
                >
                  <SortableContext
                    items={sections.map(s => `section-${s.id}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {sections.map(section => (
                        <SortableSectionCard
                          key={section.id}
                          section={section}
                          isExpanded={expandedSections.has(section.id)}
                          onToggleExpand={() => {
                            setExpandedSections(prev => {
                              const next = new Set(prev)
                              if (next.has(section.id)) next.delete(section.id)
                              else next.add(section.id)
                              return next
                            })
                          }}
                          selectedClipId={selectedClipMomentId}
                          onSelectClip={setSelectedClipMomentId}
                          onPreviewClip={handlePreviewClip}
                          onToggleLock={handleToggleLock}
                          onToggleExclude={handleToggleExclude}
                          onRemoveMoment={handleRemoveMoment}
                          onToggleSectionLock={handleToggleSectionLock}
                          onRegenerate={handleRegenerateSection}
                          onEditName={handleEditSectionName}
                          onDelete={handleDeleteSection}
                          regeneratingId={regeneratingSection}
                        />
                      ))}
                    </div>
                  </SortableContext>
                  <DragOverlay>
                    {draggedClipMoment && <ClipRowOverlay moment={draggedClipMoment} />}
                  </DragOverlay>
                </DndContext>
              )}

              {/* Add section button */}
              <button
                onClick={handleAddSection}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border border-dashed border-white/10 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] hover:border-white/20 hover:bg-white/[0.02] transition-colors text-xs"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Section
              </button>
            </>
          )}

          {/* === SEQUENCE VIEW === */}
          {viewMode === 'sequence' && (
            <>
              {/* Player */}
              <SequencePlayer
                segments={activeSegments}
                currentSegmentId={selectedSegmentId}
                onSegmentChange={id => setSelectedSegmentId(id)}
                onTimeUpdate={pos => setPlayhead({
                  assetId: pos.assetId,
                  sourceTime: pos.sourceTime,
                })}
              />

              {/* Section overview strip */}
              {assembly && sections.length > 0 && (() => {
                const sorted = [...sections].sort((a, b) => a.section_order - b.section_order)
                const momentToSection = new Map<number, number>()
                sorted.forEach((s, i) => {
                  s.moments.forEach(m => momentToSection.set(m.moment_id, i))
                })
                const sectionDurations = sorted.map(() => 0)
                activeSegments.forEach(seg => {
                  if (seg.source_moment_id != null) {
                    const sIdx = momentToSection.get(seg.source_moment_id)
                    if (sIdx !== undefined) sectionDurations[sIdx] += seg.duration
                  }
                })
                const totalDur = sectionDurations.reduce((a, b) => a + b, 0)
                if (totalDur === 0) return null
                return (
                  <div className="flex items-stretch h-8 rounded-lg overflow-hidden border border-white/5 bg-white/5">
                    {sorted.map((section, i) => {
                      const pct = (sectionDurations[i] / totalDur) * 100
                      if (pct === 0) return null
                      const colorClass = TIMELINE_COLORS[i % TIMELINE_COLORS.length]
                      return (
                        <button
                          key={section.id}
                          onClick={() => {
                            // Jump to first segment of this section
                            const firstSeg = activeSegments.find(seg =>
                              seg.source_moment_id != null && momentToSection.get(seg.source_moment_id) === i
                            )
                            if (firstSeg) setSelectedSegmentId(firstSeg.id)
                          }}
                          className={`h-full flex items-center px-2 border-r last:border-r-0 border-white/10 transition-colors hover:brightness-125 ${colorClass}`}
                          style={{ width: `${pct}%`, minWidth: '40px' }}
                          title={section.name}
                        >
                          <span className="text-[9px] text-[var(--rq-text)] truncate font-medium">
                            {section.name}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )
              })()}

              {/* Sequence header with layout toggle */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-[var(--rq-text-soft)]">
                    Sequence ({activeSegments.length})
                  </h2>
                  <div className="flex items-center gap-0.5 bg-white/5 rounded-lg p-0.5">
                    <button
                      onClick={() => setSequenceLayout('list')}
                      className={`p-1.5 rounded-md transition-colors ${
                        sequenceLayout === 'list'
                          ? 'bg-white/10 text-[var(--rq-text)]'
                          : 'text-[var(--rq-text-soft)] hover:text-[var(--rq-text)]'
                      }`}
                      title="List view"
                    >
                      <List className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setSequenceLayout('timeline')}
                      className={`p-1.5 rounded-md transition-colors ${
                        sequenceLayout === 'timeline'
                          ? 'bg-white/10 text-[var(--rq-text)]'
                          : 'text-[var(--rq-text-soft)] hover:text-[var(--rq-text)]'
                      }`}
                      title="Timeline view"
                    >
                      <LayoutGrid className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {activeSegments.length === 0 ? (
                  <div className="text-center py-12 text-[var(--rq-text-soft)] text-sm">
                    No segments in sequence.
                    {removedSegments.length > 0 && ' Check the removed pool below.'}
                  </div>
                ) : sequenceLayout === 'list' ? (
                  /* Vertical list layout */
                  <DndContext
                    id="sequence-dnd"
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleSegDragStart}
                    onDragEnd={handleSegDragEnd}
                  >
                    <SortableContext
                      items={activeSegments.map(s => `seg-${s.id}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-0.5">
                        {activeSegments.map((seg, idx) => (
                          <SortableSegmentCard
                            key={seg.id}
                            segment={seg}
                            index={idx}
                            isSelected={selectedSegmentId === seg.id}
                            sectionLabel={getSectionLabel(idx)}
                            onClick={() => setSelectedSegmentId(seg.id)}
                            onPreview={() => handlePreviewClip(seg)}
                            onRemove={() => handleRemoveSegment(seg.id)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                    <DragOverlay>
                      {draggedSegment && <SegmentOverlay segment={draggedSegment} />}
                    </DragOverlay>
                  </DndContext>
                ) : (
                  /* Horizontal timeline layout */
                  <DndContext
                    id="sequence-timeline-dnd"
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleSegDragStart}
                    onDragEnd={handleSegDragEnd}
                  >
                    <SortableContext
                      items={activeSegments.map(s => `seg-${s.id}`)}
                      strategy={horizontalListSortingStrategy}
                    >
                      {(() => {
                        const seqDuration = activeSegments.reduce((sum, s) => sum + s.duration, 0)
                        return (
                          <div className="flex items-stretch h-24 gap-0.5 overflow-x-auto pb-2 scrollbar-thin">
                            {activeSegments.map((seg, idx) => {
                              const pct = seqDuration > 0 ? (seg.duration / seqDuration) * 100 : 100 / activeSegments.length
                              return (
                                <SortableTimelineBlock
                                  key={seg.id}
                                  segment={seg}
                                  index={idx}
                                  widthPct={pct}
                                  isSelected={selectedSegmentId === seg.id}
                                  sectionLabel={getSectionLabel(idx)}
                                  sectionIndex={getSectionIndex(idx)}
                                  onClick={() => setSelectedSegmentId(seg.id)}
                                  onPreview={() => handlePreviewClip(seg)}
                                  onRemove={() => handleRemoveSegment(seg.id)}
                                />
                              )
                            })}
                          </div>
                        )
                      })()}
                    </SortableContext>
                    <DragOverlay>
                      {draggedSegment && (
                        <div className="h-24 w-32 rounded-md bg-[var(--rq-surface)] border border-white/20 shadow-xl overflow-hidden flex flex-col justify-end p-1.5">
                          {draggedSegment.thumbnail_url && (
                            <img src={draggedSegment.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
                          )}
                          <p className="relative text-[9px] text-white font-medium truncate drop-shadow-md">
                            {draggedSegment.title || draggedSegment.asset_filename || 'Untitled'}
                          </p>
                          <span className="relative text-[8px] text-white/70 tabular-nums drop-shadow-md">
                            {formatTC(draggedSegment.duration)}
                          </span>
                        </div>
                      )}
                    </DragOverlay>
                  </DndContext>
                )}
              </div>

              {/* Multi-lane timeline (feature-flagged). Inserted between
                  the SEQUENCE list and the Removed collapsible per sprint
                  brief. Derives the source asset from the first active
                  segment; falls back to no render if the assembly has no
                  segments (unlikely past generation). */}
              {TIMELINE_VIEW_ENABLED && activeSegments.length > 0 && (() => {
                const primaryAssetId = activeSegments[0].asset_id
                // TimelineView fetches the canonical asset_duration from
                // the API; we only pass a fallback derived from segment
                // out-TCs in case the backend hasn't probed yet.
                const sameAsset = activeSegments.filter(s => s.asset_id === primaryAssetId)
                const maxOutTc = sameAsset.reduce((m, s) => Math.max(m, s.out_tc || 0), 0)
                // Click a timeline block → seek the SequencePlayer to the
                // sequence clip that contains that source time. If no clip
                // covers the point (it was removed), pick the nearest one
                // on the same asset so the editor still jumps somewhere
                // meaningful.
                const handleSeek = (assetId: number, sourceTimeSecs: number) => {
                  const onAsset = activeSegments.filter(s => s.asset_id === assetId)
                  if (onAsset.length === 0) return
                  const exact = onAsset.find(
                    s => s.in_tc <= sourceTimeSecs && s.out_tc >= sourceTimeSecs
                  )
                  if (exact) {
                    setSelectedSegmentId(exact.id)
                    return
                  }
                  const nearest = onAsset.reduce((best, s) => {
                    const d = Math.min(
                      Math.abs(s.in_tc - sourceTimeSecs),
                      Math.abs(s.out_tc - sourceTimeSecs),
                    )
                    const dBest = Math.min(
                      Math.abs(best.in_tc - sourceTimeSecs),
                      Math.abs(best.out_tc - sourceTimeSecs),
                    )
                    return d < dBest ? s : best
                  })
                  setSelectedSegmentId(nearest.id)
                }
                return (
                  <div className="pt-2">
                    <TimelineView
                      assetId={primaryAssetId}
                      // Only while the asset being played is the one this
                      // timeline is drawn for. On a cut that crosses
                      // sources, a playhead from another file would point
                      // confidently at the wrong frame, so it parks at the
                      // start instead.
                      currentTimeSecs={
                        playhead?.assetId === primaryAssetId
                          ? playhead.sourceTime
                          : undefined
                      }
                      durationSecsFallback={maxOutTc > 0 ? maxOutTc : null}
                      onSeekToSource={handleSeek}
                    />
                  </div>
                )
              })()}

              {/* Removed pool */}
              <div className="border-t border-white/10 pt-3">
                <button
                  onClick={() => setShowRemovedPool(!showRemovedPool)}
                  className="flex items-center gap-2 text-xs text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors"
                >
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showRemovedPool ? '' : '-rotate-90'}`} />
                  Removed ({removedSegments.length})
                </button>

                {showRemovedPool && removedSegments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {removedSegments.map(seg => (
                      <div
                        key={seg.id}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/[0.04] opacity-60"
                      >
                        <div className="w-12 h-7 rounded overflow-hidden bg-black/40 flex-shrink-0">
                          {seg.thumbnail_url ? (
                            <img src={seg.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Film className="w-3 h-3 text-[var(--rq-text-soft)]" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-[var(--rq-text-soft)] truncate">
                            {seg.title || seg.asset_filename || 'Untitled'}
                          </p>
                          <span className="text-[10px] text-[var(--rq-text-soft)] tabular-nums">
                            {formatTC(seg.duration)}
                          </span>
                        </div>
                        <button
                          onClick={() => handleRestoreSegment(seg.id)}
                          className="flex-shrink-0 text-[10px] px-2 py-1 rounded bg-white/5 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] hover:bg-white/10 transition-colors"
                        >
                          <Plus className="w-3 h-3 inline mr-0.5" />
                          Re-add
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {showRemovedPool && removedSegments.length === 0 && (
                  <p className="mt-2 text-xs text-[var(--rq-text-soft)]">No removed segments.</p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Right panel — clip details */}
        <div className="w-80 flex-shrink-0 hidden lg:block">
          <div className="sticky top-6">
            {viewMode === 'sections' && selectedClip ? (
              <ClipDetailsPanel
                clip={selectedClip}
                sectionName={selectedClipSection?.name || null}
                onPreview={() => handlePreviewClip(selectedClip)}
              />
            ) : viewMode === 'sequence' && selectedSegment ? (
              <ClipDetailsPanel
                clip={selectedSegment}
                sectionName={getSectionLabel(
                  activeSegments.findIndex(s => s.id === selectedSegment.id)
                )}
                onPreview={() => handlePreviewClip(selectedSegment)}
              />
            ) : (
              <div className="text-center py-12">
                <Info className="w-6 h-6 text-[var(--rq-text-soft)] mx-auto mb-2" />
                <p className="text-xs text-[var(--rq-text-soft)]">
                  Click a clip to view details
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <MediaPreviewModal preview={preview} onClose={closePreview} />
    </div>
  )
}
